// REQ-P1-06b: アーティストチャット画面の契約型付きAPI。

import { api, type paths } from './client';
import type { operations } from './types.gen';
import { ApiStatusError } from './templates';

type ArtistMentionCandidatesQuery = NonNullable<
  paths['/artist/chat-mentions/admin-candidates']['get']['parameters']['query']
>;
type ArtistMentionCandidatesResponse = NonNullable<
  paths['/artist/chat-mentions/admin-candidates']['get']['responses'][200]['content']
>['application/json'];
type ArtistPendingMentionTargetsQuery = NonNullable<
  paths['/artist/chat-mentions/pending']['get']['parameters']['query']
>;
type ArtistPendingMentionTargetsResponse = NonNullable<
  paths['/artist/chat-mentions/pending']['get']['responses'][200]['content']
>['application/json'];
type ArtistPendingMentionItemsQuery = NonNullable<
  paths['/artist/chat-mentions/pending-items']['get']['parameters']['query']
>;
type ArtistPendingMentionItemsResponse = NonNullable<
  paths['/artist/chat-mentions/pending-items']['get']['responses'][200]['content']
>['application/json'];
type ResolveArtistMentionTargetRequest = NonNullable<
  paths['/artist/chat-mentions/resolve-target']['post']['requestBody']
>['content']['application/json'];
type ResolveArtistMentionItemRequest = NonNullable<
  paths['/artist/chat-mentions/resolve-item']['post']['requestBody']
>['content']['application/json'];
type ArtistMessagesResponse = NonNullable<
  paths['/artist/messages/{offset}/{limit}']['get']['responses'][200]['content']
>['application/json'];
type ArtistMessagesAroundResponse = NonNullable<
  paths['/artist/messages/around/{messageId}/{before}/{after}']['get']['responses'][200]['content']
>['application/json'];
type ArtistMessagesBeforeResponse = NonNullable<
  paths['/artist/messages/before/{beforeCreatedAt}/{limit}']['get']['responses'][200]['content']
>['application/json'];
type ArtistNewMessagesResponse = NonNullable<
  paths['/artist/new-messages/{lastCreatedAt}']['get']['responses'][200]['content']
>['application/json'];
type ArtistMessageSearchQuery = NonNullable<
  paths['/artist/messages/search']['get']['parameters']['query']
>;
type ArtistMessageSearchResponse = NonNullable<
  paths['/artist/messages/search']['get']['responses'][200]['content']
>['application/json'];
type ArtistTextMessageRequest = NonNullable<
  paths['/artist/messages/text']['post']['requestBody']
>['content']['application/json'];
type ArtistGroupChatThreadsQuery = NonNullable<
  paths['/artist/group-chat/threads']['get']['parameters']['query']
>;
type ArtistGroupChatThreadsResponse = NonNullable<
  paths['/artist/group-chat/threads']['get']['responses'][200]['content']
>['application/json'];
type ArtistGroupChatThreadResponse = NonNullable<
  paths['/artist/group-chat/threads/{threadId}']['get']['responses'][200]['content']
>['application/json'];
type ArtistGroupChatMessagesResponse = NonNullable<
  paths['/artist/group-chat/threads/{threadId}/messages/{offset}/{limit}']['get']['responses'][200]['content']
>['application/json'];
type ArtistGroupChatMessagesAroundResponse = NonNullable<
  paths['/artist/group-chat/threads/{threadId}/messages/around/{messageId}/{before}/{after}']['get']['responses'][200]['content']
>['application/json'];
type MarkArtistGroupChatReadRequest = NonNullable<
  paths['/artist/group-chat/read']['post']['requestBody']
>['content']['application/json'];
type ArtistGroupChatTextMessageRequest = NonNullable<
  paths['/artist/group-chat/threads/{threadId}/messages/text']['post']['requestBody']
>['content']['application/json'];
type ArtistGroupChatImageUploadRequest = NonNullable<
  paths['/artist/group-chat/threads/{threadId}/messages/images']['post']['requestBody']
>['content']['multipart/form-data'];
type ArtistGroupChatFileUploadRequest = NonNullable<
  paths['/artist/group-chat/threads/{threadId}/messages/files']['post']['requestBody']
>['content']['multipart/form-data'];
type ArtistImageMessageUploadRequest = NonNullable<
  paths['/artist/messages/images']['post']['requestBody']
>['content']['multipart/form-data'];
type ArtistFileMessageUploadRequest = NonNullable<
  paths['/artist/messages/files']['post']['requestBody']
>['content']['multipart/form-data'];
type ArtistHoldCastingsResponse = NonNullable<
  paths['/artist/hold-castings']['get']['responses'][200]['content']
>['application/json'];
type ArtistContractsResponse = NonNullable<
  paths['/artist/contracts']['get']['responses'][200]['content']
>['application/json'];
type ArtistContractsByPeriodQuery = NonNullable<
  operations['listArtistContractsByPeriod']['parameters']['query']
>;
type ArtistContractsByPeriodResponse = NonNullable<
  operations['listArtistContractsByPeriod']['responses'][200]['content']
>['application/json'];
type ArtistPersonalUnreadCountResponse = NonNullable<
  paths['/artist/messages/unread-count']['get']['responses'][200]['content']
>['application/json'];
type ArtistGroupChatUnreadCountResponse = NonNullable<
  paths['/artist/group-chat/unread-count']['get']['responses'][200]['content']
>['application/json'];

function ensureData<T>(data: T | undefined, status: number, ok: boolean): T {
  if (!ok || !data) throw new ApiStatusError(status);
  return data;
}

function ensureOk(status: number, ok: boolean) {
  if (!ok) throw new ApiStatusError(status);
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwStatusError(status: number, error?: unknown): never {
  const statusError = new ApiStatusError(status);
  const message = getApiErrorMessage(error);
  if (message) statusError.message = message;
  throw statusError;
}

function ensureApiData<T>(data: T | undefined, error: unknown, response: Response): T {
  if (!response.ok || !data) throwStatusError(response.status, error);
  return data;
}

function ensureApiOk(data: unknown, error: unknown, response: Response) {
  if (!response.ok) throwStatusError(response.status, error);
  return readOptionalJson(data, response);
}

async function readOptionalJson(data: unknown, response: Response) {
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch (_error) {
    return {};
  }
}

export async function listArtistMentionCandidates(
  query?: ArtistMentionCandidatesQuery
): Promise<ArtistMentionCandidatesResponse> {
  const { data, response } = await api.GET('/artist/chat-mentions/admin-candidates', {
    params: query ? { query } : undefined,
  });
  return ensureData(data as ArtistMentionCandidatesResponse | undefined, response.status, response.ok);
}

export async function listArtistPendingMentionTargets(
  query: ArtistPendingMentionTargetsQuery
): Promise<ArtistPendingMentionTargetsResponse> {
  const { data, response } = await api.GET('/artist/chat-mentions/pending', {
    params: { query },
  });
  return ensureData(data as ArtistPendingMentionTargetsResponse | undefined, response.status, response.ok);
}

export async function listArtistPendingMentionItems(
  query: ArtistPendingMentionItemsQuery
): Promise<ArtistPendingMentionItemsResponse> {
  const { data, response } = await api.GET('/artist/chat-mentions/pending-items', {
    params: { query },
  });
  return ensureData(data as ArtistPendingMentionItemsResponse | undefined, response.status, response.ok);
}

export async function resolveArtistMentionTarget(body: ResolveArtistMentionTargetRequest) {
  const { response } = await api.POST('/artist/chat-mentions/resolve-target', {
    body,
  });
  ensureOk(response.status, response.ok);
}

export async function resolveArtistMentionItem(body: ResolveArtistMentionItemRequest) {
  const { response } = await api.POST('/artist/chat-mentions/resolve-item', {
    body,
  });
  ensureOk(response.status, response.ok);
}

export async function listArtistMessages(
  offset: number,
  limit: number
): Promise<ArtistMessagesResponse> {
  const { data, response } = await api.GET('/artist/messages/{offset}/{limit}', {
    params: { path: { offset, limit } },
  });
  return ensureData(data as ArtistMessagesResponse | undefined, response.status, response.ok);
}

export async function listArtistMessagesAround(
  messageId: string,
  before: number,
  after: number
): Promise<ArtistMessagesAroundResponse> {
  const { data, response } = await api.GET(
    '/artist/messages/around/{messageId}/{before}/{after}',
    {
      params: { path: { messageId, before, after } },
    }
  );
  return ensureData(data as ArtistMessagesAroundResponse | undefined, response.status, response.ok);
}

export async function listArtistMessagesBefore(
  beforeCreatedAt: string,
  limit: number
): Promise<ArtistMessagesBeforeResponse> {
  const { data, response } = await api.GET(
    '/artist/messages/before/{beforeCreatedAt}/{limit}',
    {
      params: { path: { beforeCreatedAt, limit } },
    }
  );
  return ensureData(data as ArtistMessagesBeforeResponse | undefined, response.status, response.ok);
}

export async function listArtistNewMessages(
  lastCreatedAt: string
): Promise<ArtistNewMessagesResponse> {
  const { data, response } = await api.GET('/artist/new-messages/{lastCreatedAt}', {
    params: { path: { lastCreatedAt } },
  });
  return ensureData(data as ArtistNewMessagesResponse | undefined, response.status, response.ok);
}

export async function searchArtistMessages(
  query: ArtistMessageSearchQuery
): Promise<ArtistMessageSearchResponse> {
  const { data, response } = await api.GET('/artist/messages/search', {
    params: { query },
  });
  return ensureData(data as ArtistMessageSearchResponse | undefined, response.status, response.ok);
}

export async function listArtistGroupChatThreads(
  query?: ArtistGroupChatThreadsQuery
): Promise<ArtistGroupChatThreadsResponse> {
  const { data, response } = await api.GET('/artist/group-chat/threads', {
    params: query ? { query } : undefined,
  });
  return ensureData(data as ArtistGroupChatThreadsResponse | undefined, response.status, response.ok);
}

export async function getArtistGroupChatThread(
  threadId: string
): Promise<ArtistGroupChatThreadResponse> {
  const { data, error, response } = await api.GET('/artist/group-chat/threads/{threadId}', {
    params: { path: { threadId } },
  });
  return ensureApiData(data as ArtistGroupChatThreadResponse | undefined, error, response);
}

export async function listArtistGroupChatMessages(
  threadId: string,
  offset: number,
  limit: number
): Promise<ArtistGroupChatMessagesResponse> {
  const { data, error, response } = await api.GET(
    '/artist/group-chat/threads/{threadId}/messages/{offset}/{limit}',
    {
      params: { path: { threadId, offset, limit } },
    }
  );
  return ensureApiData(data as ArtistGroupChatMessagesResponse | undefined, error, response);
}

export async function listArtistGroupChatMessagesAround(
  threadId: string,
  messageId: string,
  before: number,
  after: number
): Promise<ArtistGroupChatMessagesAroundResponse> {
  const { data, error, response } = await api.GET(
    '/artist/group-chat/threads/{threadId}/messages/around/{messageId}/{before}/{after}',
    {
      params: { path: { threadId, messageId, before, after } },
    }
  );
  return ensureApiData(data as ArtistGroupChatMessagesAroundResponse | undefined, error, response);
}

export async function markArtistGroupChatMessagesRead(
  body?: MarkArtistGroupChatReadRequest
) {
  const { data, error, response } = await api.POST('/artist/group-chat/read', {
    ...(body ? { body } : {}),
  });
  return ensureApiOk(data, error, response);
}

export async function markArtistMessagesRead() {
  const { response } = await api.POST('/artist/messages/is-read');
  ensureOk(response.status, response.ok);
}

export async function getArtistPersonalUnreadCount(): Promise<ArtistPersonalUnreadCountResponse> {
  const { data, response } = await api.GET('/artist/messages/unread-count');
  return ensureData(data as ArtistPersonalUnreadCountResponse | undefined, response.status, response.ok);
}

export async function getArtistGroupChatUnreadCount(): Promise<ArtistGroupChatUnreadCountResponse> {
  const { data, response } = await api.GET('/artist/group-chat/unread-count');
  return ensureData(data as ArtistGroupChatUnreadCountResponse | undefined, response.status, response.ok);
}

export async function deleteArtistMessage(messageId: string) {
  const { data, response } = await api.DELETE('/artist/messages/{messageId}', {
    params: { path: { messageId } },
  });
  ensureOk(response.status, response.ok);
  return readOptionalJson(data, response);
}

export async function deleteArtistGroupChatMessage(threadId: string, messageId: string) {
  const { data, error, response } = await api.DELETE(
    '/artist/group-chat/threads/{threadId}/messages/{messageId}',
    {
      params: { path: { threadId, messageId } },
    }
  );
  return ensureApiOk(data, error, response);
}

export async function sendArtistTextMessage(body: ArtistTextMessageRequest, idempotencyKey?: string) {
  const { data, error, response } = await api.POST('/artist/messages/text', {
    body,
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
  return ensureApiOk(data, error, response);
}

export async function sendArtistImageMessages(body: ArtistImageMessageUploadRequest) {
  const { response } = await api.POST('/artist/messages/images', {
    body,
  });
  ensureOk(response.status, response.ok);
}

export async function sendArtistFileMessage(body: ArtistFileMessageUploadRequest) {
  const { response } = await api.POST('/artist/messages/files', {
    body,
  });
  ensureOk(response.status, response.ok);
}

export async function sendArtistGroupChatTextMessage(
  threadId: string,
  body: ArtistGroupChatTextMessageRequest,
  idempotencyKey?: string
) {
  const { data, error, response } = await api.POST(
    '/artist/group-chat/threads/{threadId}/messages/text',
    {
      params: { path: { threadId } },
      body,
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    }
  );
  return ensureApiOk(data, error, response);
}

export async function sendArtistGroupChatImageMessages(
  threadId: string,
  body: ArtistGroupChatImageUploadRequest
) {
  const { data, error, response } = await api.POST(
    '/artist/group-chat/threads/{threadId}/messages/images',
    {
      params: { path: { threadId } },
      body,
    }
  );
  return ensureApiOk(data, error, response);
}

export async function sendArtistGroupChatFileMessage(
  threadId: string,
  body: ArtistGroupChatFileUploadRequest
) {
  const { data, error, response } = await api.POST(
    '/artist/group-chat/threads/{threadId}/messages/files',
    {
      params: { path: { threadId } },
      body,
    }
  );
  return ensureApiOk(data, error, response);
}

export async function listArtistHoldCastings(): Promise<ArtistHoldCastingsResponse> {
  const { data, response } = await api.GET('/artist/hold-castings');
  return ensureData(data as ArtistHoldCastingsResponse | undefined, response.status, response.ok);
}

export async function listArtistContracts(): Promise<ArtistContractsResponse> {
  const { data, response } = await api.GET('/artist/contracts');
  return ensureData(data as ArtistContractsResponse | undefined, response.status, response.ok);
}

export async function listArtistContractsByPeriod(
  query: ArtistContractsByPeriodQuery
): Promise<ArtistContractsByPeriodResponse> {
  const { data, response } = await api.GET('/artist/contracts/period', {
    params: { query },
  });
  return ensureData(data as ArtistContractsByPeriodResponse | undefined, response.status, response.ok);
}
